import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { ChevronLeft, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import { mandantenAPI, reportsAPI } from '../lib/api';

function fmtMoney(v) {
  const n = Number(v);
  if (!n) return '';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n);
}

function fmtDate(s) {
  if (!s) return '—';
  return format(new Date(s), 'dd.MM.yyyy');
}

export default function Kontenblatt() {
  const { slug, konto } = useParams();
  const [searchParams] = useSearchParams();
  const [mandant, setMandant] = useState(null);
  const [from, setFrom] = useState(searchParams.get('from') || '');
  const [to, setTo] = useState(searchParams.get('to') || '');
  const [includeVorschlag, setIncludeVorschlag] = useState(
    searchParams.get('include_vorgeschlagen') === 'true'
  );
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    mandantenAPI.get(slug).then((r) => setMandant(r.data)).catch(() => {});
  }, [slug]);

  const reload = useCallback(() => {
    setLoading(true);
    return reportsAPI
      .kontenblatt(slug, konto, {
        from: from || undefined,
        to: to || undefined,
        include_vorgeschlagen: includeVorschlag,
      })
      .then((r) => setItems(r.data))
      .catch((err) =>
        toast.error(err.response?.data?.detail || 'Kontenblatt konnte nicht geladen werden')
      )
      .finally(() => setLoading(false));
  }, [slug, konto, from, to, includeVorschlag]);

  useEffect(() => { reload(); }, [reload]);

  const totals = items.reduce(
    (acc, r) => {
      acc.soll += Number(r.soll);
      acc.haben += Number(r.haben);
      return acc;
    },
    { soll: 0, haben: 0 }
  );
  const saldo = totals.soll - totals.haben;

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <Link to={`/mandanten/${slug}/reports`} className="back-link">
            <ChevronLeft size={14} /> Summen- und Saldenliste
          </Link>
          <h1>Kontenblatt {konto}</h1>
          {mandant && (
            <div className="muted">{mandant.name} · {mandant.skr_variant}</div>
          )}
        </div>
      </header>

      <div className="filters" style={{ flexWrap: 'wrap' }}>
        <label>
          Von: <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label>
          Bis: <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <input
            type="checkbox"
            checked={includeVorschlag}
            onChange={(e) => setIncludeVorschlag(e.target.checked)}
            style={{ width: 'auto' }}
          />
          Vorschläge einbeziehen
        </label>
        <button className="btn-text" onClick={reload} disabled={loading}>
          {loading ? 'Lädt…' : 'Neu laden'}
        </button>
      </div>

      {loading ? (
        <div className="muted">Lädt…</div>
      ) : items.length === 0 ? (
        <div className="card empty">Keine Buchungen für Konto {konto} im Zeitraum.</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Datum</th>
              <th>Quelle</th>
              <th>Gegenkonto</th>
              <th>Soll</th>
              <th>Haben</th>
              <th>Buchungstext</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id}>
                <td>{fmtDate(r.beleg_datum)}</td>
                <td>
                  {r.beleg_id ? (
                    <Link to={`/mandanten/${slug}/belege/${r.beleg_id}`} className="quelle-link" title={r.belegnummer || ''}>
                      <FileText size={12} /> Eingang #{r.beleg_id}
                    </Link>
                  ) : r.ausgangsrechnung_id ? (
                    <span className="quelle-link" title={r.belegnummer || ''}>
                      <FileText size={12} /> Ausgang #{r.ausgangsrechnung_id}
                    </span>
                  ) : (
                    <span className="muted small">manuell</span>
                  )}
                </td>
                <td className="num">
                  <Link
                    to={`/mandanten/${slug}/reports/konto/${r.gegenkonto}?from=${from}&to=${to}${includeVorschlag ? '&include_vorgeschlagen=true' : ''}`}
                    className="quelle-link"
                  >
                    {r.gegenkonto}
                  </Link>
                </td>
                <td className="num">{fmtMoney(r.soll)}</td>
                <td className="num">{fmtMoney(r.haben)}</td>
                <td>
                  {r.buchungstext || '—'}
                  {r.partner_name && (
                    <span className="muted small" style={{ marginLeft: 8 }}>· {r.partner_name}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: '2px solid #e2e8f0', fontWeight: 600 }}>
              <td colSpan={3}>Summe ({items.length} Buchungen)</td>
              <td className="num">{fmtMoney(totals.soll)}</td>
              <td className="num">{fmtMoney(totals.haben)}</td>
              <td className="muted">
                Saldo: {saldo > 0 ? fmtMoney(saldo) + ' Soll' : saldo < 0 ? fmtMoney(-saldo) + ' Haben' : '0,00 €'}
              </td>
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  );
}
